export type NonEmptyArray<T = any> = [T, ...T[]];

/** 배열에서 특정 값이 있는지 찾아서 있으면 그 값을 반환하고 없으면 배열의 첫번째 값을 반환한다.
 * @param list 배열
 * @param value 찾을 값 (nullable)
 * @param key 찾을 값의 키
 */
export function findValueOrReturnFirst<T extends object, K extends keyof T>(
  list: NonEmptyArray<T>,
  value: T[K] | null,
  key: K
): T[K];
export function findValueOrReturnFirst<T extends object, K extends keyof T>(
  list: T[],
  value: T[K] | null,
  key: K
): T[K] | undefined;
export function findValueOrReturnFirst<T extends object, K extends keyof T>(
  list: T[],
  value: T[K] | null,
  key: K
): T[K] | undefined {
  const found = list.find((item) => item[key] === value);
  const [first] = list;
  return (found || (first as T | undefined))?.[key];
}

/** 배열이 비어있지 않다고 단언 */
type AssertNonEmptyArray = <T>(arr: T[]) => asserts arr is NonEmptyArray<T>;
export const assertNonEmptyArray: AssertNonEmptyArray = (arr) => {
  if (arr.length === 0) {
    throw new Error('Array is empty');
  }
};

/** 재귀적으로 partial 적용 */
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export const resolveObject = <T extends Record<string, Promise<unknown> | undefined>>(obj: T) =>
  Promise.all(Object.entries(obj).map(async ([k, v]) => [k, await v])).then(Object.fromEntries) as Promise<{
    [K in keyof T]: Awaited<T[K]>;
  }>;
